// Powers, Roots & Radicals helpers: square/cube/nth roots, exponents,
// radical simplification, scientific notation, absolute value, logarithms,
// exponential growth/decay, perfect square/cube checking, rational
// (fractional) exponents, and root mean square. Pure algebra - mostly no
// natural-language output, since these are direct numeric transformations
// rather than statuses or generated summaries.

function parseNumberList(raw: unknown): number[] {
    return String(raw ?? '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map(Number)
        .filter((v) => !isNaN(v))
}

// ============================================================
// 1. Square Root Calculator
// ============================================================
export function squareRootCalculator(params: { number: unknown }): { result: number | string; is_perfect_square: string } {
    const n = Number(params.number) || 0
    if (n < 0) {
        return { result: `${(Math.sqrt(-n)).toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}i`, is_perfect_square: 'No' }
    }
    const value = Math.sqrt(n)
    const rounded = Math.round(value)
    const isPerfect = rounded * rounded === n
    return { result: value, is_perfect_square: isPerfect ? 'Yes' : 'No' }
}

// ============================================================
// 2. Cube Root Calculator
// ============================================================
export function cubeRootCalculator(params: { number: unknown }): { result: number; is_perfect_cube: string } {
    const n = Number(params.number) || 0
    const value = Math.sign(n) * Math.pow(Math.abs(n), 1 / 3)
    const rounded = Math.round(value)
    const isPerfect = rounded * rounded * rounded === n
    return { result: value, is_perfect_cube: isPerfect ? 'Yes' : 'No' }
}

// ============================================================
// 3. Nth Root Calculator
// ============================================================
export function nthRootCalculator(params: { number: unknown; degree: unknown }): { result: number | string } {
    const x = Number(params.number) || 0
    const n = Math.max(Math.round(Number(params.degree)) || 2, 1)
    if (x < 0 && n % 2 === 0) {
        return { result: 'undefined (no real root)' }
    }
    const value = x < 0 ? -Math.pow(-x, 1 / n) : Math.pow(x, 1 / n)
    return { result: value }
}

// ============================================================
// 4. Exponent (Power) Calculator
// ============================================================
export function exponentCalculator(params: { base: unknown; exponent: unknown }): { result: number | string } {
    const base = Number(params.base) || 0
    const exponent = Number(params.exponent) || 0
    if (base < 0 && !Number.isInteger(exponent)) {
        return { result: 'undefined (complex result)' }
    }
    return { result: Math.pow(base, exponent) }
}

// ============================================================
// 5. Radical Simplifier
// ============================================================
export function radicalSimplifier(params: { radicand: unknown; degree: unknown }): {
    coefficient: number; remaining_radicand: number; simplified_form: string; decimal_value: number
} {
    const degree = Math.max(Math.round(Number(params.degree)) || 2, 2)
    const radicand = Math.round(Number(params.radicand)) || 0
    const negative = radicand < 0
    let remainder = Math.abs(radicand)
    let coefficient = 1

    if (remainder > 0) {
        for (let factor = 2; Math.pow(factor, degree) <= remainder; factor++) {
            const power = Math.pow(factor, degree)
            while (remainder % power === 0) {
                coefficient *= factor
                remainder = remainder / power
            }
        }
    }

    if (negative) {
        // Odd degree: the negative sign can move outside the radical (real result)
        // Even degree: leaving the radicand negative signals an undefined real result
        if (degree % 2 !== 0) coefficient = -coefficient
        else remainder = -remainder
    }

    const rootSymbol = degree === 2 ? '√' : degree === 3 ? '∛' : `${degree}√`
    const simplifiedForm =
        remainder === 1 ? String(coefficient) : coefficient === 1 ? `${rootSymbol}${remainder}` : `${coefficient}${rootSymbol}${remainder}`
    const decimalValue =
        remainder === 1
            ? coefficient
            : remainder < 0
                ? NaN
                : coefficient * Math.pow(remainder, 1 / degree)

    return { coefficient, remaining_radicand: remainder, simplified_form: simplifiedForm, decimal_value: decimalValue }
}

// ============================================================
// 6. Scientific Notation Calculator
// ============================================================
export function scientificNotationCalculator(params: {
    mode: unknown; number: unknown; coefficient: unknown; notation_exponent: unknown
}): { scientific_notation: string; standard_notation: string } {
    const mode = String(params.mode || 'to_scientific')

    if (mode === 'from_scientific') {
        const coefficient = Number(params.coefficient) || 0
        const exponent = Math.round(Number(params.notation_exponent)) || 0
        const standard = coefficient * Math.pow(10, exponent)
        return {
            scientific_notation: `${coefficient} × 10^${exponent}`,
            standard_notation: String(standard),
        }
    }

    const n = Number(params.number) || 0
    if (n === 0) return { scientific_notation: '0 × 10^0', standard_notation: '0' }
    const exponent = Math.floor(Math.log10(Math.abs(n)))
    const coefficient = n / Math.pow(10, exponent)
    return {
        scientific_notation: `${Number(coefficient.toFixed(6))} × 10^${exponent}`,
        standard_notation: String(n),
    }
}

// ============================================================
// 7. Absolute Value Calculator
// ============================================================
export function absoluteValueCalculator(params: { number: unknown }): { result: number } {
    const n = Number(params.number) || 0
    return { result: Math.abs(n) }
}

// ============================================================
// 8. Logarithm Calculator
// ============================================================
export function logarithmCalculator(params: { number: unknown; base: unknown }): { result: number | string } {
    const x = Number(params.number)
    const base = Number(params.base) || 10
    if (!(x > 0) || !(base > 0) || base === 1) {
        return { result: 'undefined (number must be > 0, base must be > 0 and ≠ 1)' }
    }
    return { result: Math.log(x) / Math.log(base) }
}

// ============================================================
// 9. Exponential Growth/Decay Calculator
// ============================================================
export function exponentialGrowthDecayCalculator(params: {
    initial_value: unknown; rate_percent: unknown; time_periods: unknown
}): { final_value: number; total_change_percent: number } {
    const principal = Number(params.initial_value) || 0
    const rate = Number(params.rate_percent) || 0
    const time = Number(params.time_periods) || 0
    const finalValue = principal * Math.pow(1 + rate / 100, time)
    const totalChangePercent = principal !== 0 ? ((finalValue - principal) / principal) * 100 : 0
    return { final_value: finalValue, total_change_percent: totalChangePercent }
}

// ============================================================
// 10. Perfect Square & Cube Checker
// ============================================================
export function perfectSquareCubeChecker(params: { number: unknown }): {
    is_perfect_square: string; is_perfect_cube: string; square_root_if_perfect: string; cube_root_if_perfect: string
} {
    const n = Math.round(Number(params.number)) || 0

    const sqrtRounded = n >= 0 ? Math.round(Math.sqrt(n)) : 0
    const isPerfectSquare = n >= 0 && sqrtRounded * sqrtRounded === n

    const cbrtRounded = Math.round(Math.sign(n) * Math.pow(Math.abs(n), 1 / 3))
    const isPerfectCube = cbrtRounded * cbrtRounded * cbrtRounded === n

    return {
        is_perfect_square: isPerfectSquare ? 'Yes' : 'No',
        is_perfect_cube: isPerfectCube ? 'Yes' : 'No',
        square_root_if_perfect: isPerfectSquare ? String(sqrtRounded) : '—',
        cube_root_if_perfect: isPerfectCube ? String(cbrtRounded) : '—',
    }
}

// ============================================================
// 11. Rational (Fractional) Exponent Calculator
// ============================================================
export function rationalExponentCalculator(params: {
    base: unknown; numerator: unknown; denominator: unknown
}): { result: number | string; radical_form: string } {
    const base = Number(params.base) || 0
    const numerator = Math.round(Number(params.numerator)) || 1
    const denominator = Math.max(Math.round(Number(params.denominator)) || 1, 1)
    const rootSymbol = denominator === 2 ? '√' : denominator === 3 ? '∛' : `${denominator}√`
    const radicalForm = `(${rootSymbol}${base})^${numerator}`

    if (base < 0 && denominator % 2 === 0) {
        return { result: 'undefined (complex result)', radical_form: radicalForm }
    }

    const exponent = numerator / denominator
    let value: number
    if (base < 0) {
        const absResult = Math.pow(Math.abs(base), exponent)
        value = numerator % 2 === 0 ? absResult : -absResult
    } else {
        value = Math.pow(base, exponent)
    }
    return { result: value, radical_form: radicalForm }
}

// ============================================================
// 12. Root Mean Square (RMS) Calculator
// ============================================================
export function rootMeanSquareCalculator(params: { values_list: unknown }): { rms_value: number } {
    const values = parseNumberList(params.values_list)
    if (values.length === 0) return { rms_value: 0 }
    const sumSquares = values.reduce((sum, v) => sum + v * v, 0)
    return { rms_value: Math.sqrt(sumSquares / values.length) }
}
